import { tv } from '../../utils/tv';

/**
 * Shared elevation (drop-shadow + optional surface colour) styles.
 *
 * Applies a box shadow, and optionally the elevated surface background colour, to an element.
 * Returns a plain class string — there are no slots.
 *
 * ## Variants
 *
 * - `elevation: 1` — `shadow-level-1`; subtle lift
 * - `elevation: 2` — `shadow-level-2`; medium lift
 * - `elevation: 3` — `shadow-level-3`; strong lift
 * - `withSurface: true` — also applies `surface-neutral-layer-3` background
 * - `withSurface: false` — shadow only; use when the element provides its own background
 *
 * ## Usage
 *
 * ```tsx
 * // Shadow + surface background
 * <div className={elevationStyles({ elevation: 2, withSurface: true })}>...</div>
 *
 * // Shadow only — element has its own background colour
 * <div className={elevationStyles({ elevation: 2, withSurface: false })}>...</div>
 *
 * // Merge with an external className
 * <div className={elevationStyles({ elevation: 2, withSurface: true, className })}>...</div>
 * ```
 */
export const elevationStyles = tv({
  variants: {
    elevation: {
      1: 'shadow-level-1',
      2: 'shadow-level-2',
      3: 'shadow-level-3',
    },
    withSurface: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      withSurface: true,
      elevation: [1, 2, 3],
      class: 'surface-neutral-layer-3',
    },
  ],
});

export type Elevation = 1 | 2 | 3;
